// FTR F19 — workbook → pending exports → PDF gen → zip download
// thin orchestrator: delegates to focused sub-components

import {useState, useCallback, useRef, useEffect, useMemo} from 'react'
import {Icon} from '@ui/lib/primitive/Icon'
import {field_sheet_parse_from_buffer} from '@ts/mod/excel_gen/field_sheet_parse'
import {csa_flow_analyze, build_basic_doc_input, pending_doc_net_total, str, type PendingDoc} from '@ts/mod/ftr_f19/basic/csa_flow/core'
import type {AppState, ExportedDoc, GeneratedDoc, HighlightTarget, ModalState} from './types'
import {ensureSpinKeyframes} from './utils'
import {S} from './styles'
import {DropArea} from './DropArea'
import {SheetTabs} from './SheetTabs'
import {DocTable} from './DocTable'
import {Modal} from './Modal'

// --

export function FtrF19Page() {
	const [state, setState] = useState<AppState>({stage: 'idle'})
	const [modal, setModal] = useState<ModalState>(null)
	const [selected, setSelected] = useState<Set<string>>(new Set())
	const fileInputRef = useRef<HTMLInputElement>(null)

	useEffect(() => { ensureSpinKeyframes() }, [])

	// -- file handling --

	const handleFile = useCallback(async (file: File) => {
		setState({stage: 'loading'})
		try {
			const buffer = await file.arrayBuffer()
			const sheets = await field_sheet_parse_from_buffer(buffer)
			const {pending_docs, exported_shorts, stats} = csa_flow_analyze(sheets)
			const exporter_sheet = sheets.find(s => s.name === 'Exporter')
			const exported: ExportedDoc[] = exported_shorts.map(short => {
				const row = exporter_sheet?.rows.find(r => str(r['export_batch_item.doc']) === short)
				return {
					short,
					exported_at: row ? str(row['export_batch_item.exported_at']) : '',
					author: row ? str(row['export_batch_item.author']) : '',
					total_amount: row ? Number(row['export_batch_item.total_amount']) || 0 : 0,
					ccy: row ? str(row['export_batch_item.ccy']) || 'SEK' : 'SEK',
					line_count: row ? Number(row['export_batch_item.line_count']) || 0 : 0,
				}
			})
			setSelected(new Set(pending_docs.map(d => d.short)))
			setState({stage: 'parsed', filename: file.name, sheets, stats, pending: pending_docs, exported, buffer})
		} catch (err) {
			setState({stage: 'error', message: err instanceof Error ? err.message : String(err)})
		}
	}, [])

	const handleDrop = useCallback((e: React.DragEvent) => {
		e.preventDefault()
		const file = e.dataTransfer.files[0]
		if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) handleFile(file)
	}, [handleFile])

	const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (file) handleFile(file)
	}, [handleFile])

	// -- selection --

	const pendingShorts = useMemo(() => {
		if (state.stage === 'parsed') return state.pending.map(d => d.short)
		if (state.stage === 'done') return state.allPending.map(d => d.short)
		return []
	}, [state])

	const toggleSelect = useCallback((short: string) => {
		setSelected(prev => {
			const next = new Set(prev)
			if (next.has(short)) next.delete(short)
			else next.add(short)
			return next
		})
	}, [])

	const toggleSelectAll = useCallback(() => {
		setSelected(prev => prev.size === pendingShorts.length ? new Set() : new Set(pendingShorts))
	}, [pendingShorts])

	// -- generate PDFs --

	const handleGenerate = useCallback(async () => {
		if (state.stage !== 'parsed') return
		const {pending, sheets, filename, exported, buffer} = state
		const selectedPending = pending.filter(d => selected.has(d.short))
		if (selectedPending.length === 0) return
		const dok_typer = sheets.find(s => s.name === 'Dok.typer')
		const kontakter = sheets.find(s => s.name === 'Kontakter')

		setState({stage: 'generating', filename, selected: selectedPending, progress: 0, total: selectedPending.length, sheets, allPending: pending, allExported: exported, buffer})

		try {
			const {renderToPdf} = await import('@ts/mod/pdf/layout-renderer')
			const {processTemplate} = await import('@ts/mod/layout/template')
			const {sa_template_def, sf_template_def, basic_doc_to_template_data} = await import('@ts/mod/ftr_f19/basic/mod')

			const results: GeneratedDoc[] = []

			for (let i = 0; i < selectedPending.length; i++) {
				const doc = selectedPending[i]!
				setState(prev => prev.stage === 'generating' ? {...prev, progress: i} : prev)

				const basic_inp = build_basic_doc_input(doc, dok_typer, kontakter)
				const template_data = basic_doc_to_template_data(basic_inp)
				const template_def = doc.seq_typ === 'SF' ? sf_template_def : sa_template_def
				const doc_node = processTemplate(template_def, {...template_data, _assets: {}})!

				const result = await renderToPdf(doc_node, {margin: {top: 50, right: 50, bottom: 50, left: 50}})

				results.push({
					short: doc.short,
					seq_typ: doc.seq_typ,
					author: str(doc.doc_row['payout_doc.author']),
					net_total: pending_doc_net_total(doc),
					ccy: str(doc.doc_row['payout_doc.ccy']) || 'SEK',
					line_count: doc.lines.length,
					pdf_bytes: result.pdfBytes,
					page_count: result.pageCount,
				})
			}

			setState({stage: 'done', filename, results, sheets, allPending: pending, allExported: exported, buffer})
		} catch (err) {
			setState({stage: 'error', message: err instanceof Error ? err.message : String(err)})
		}
	}, [state, selected])

	// -- generate single PDF --

	const handleGenerateOne = useCallback(async (doc: PendingDoc) => {
		if (state.stage !== 'parsed') return
		const {sheets, filename, pending, exported, buffer} = state
		const dok_typer = sheets.find(s => s.name === 'Dok.typer')
		const kontakter = sheets.find(s => s.name === 'Kontakter')

		try {
			const {renderToPdf} = await import('@ts/mod/pdf/layout-renderer')
			const {processTemplate} = await import('@ts/mod/layout/template')
			const {sa_template_def, sf_template_def, basic_doc_to_template_data} = await import('@ts/mod/ftr_f19/basic/mod')

			const basic_inp = build_basic_doc_input(doc, dok_typer, kontakter)
			const template_data = basic_doc_to_template_data(basic_inp)
			const template_def = doc.seq_typ === 'SF' ? sf_template_def : sa_template_def
			const doc_node = processTemplate(template_def, {...template_data, _assets: {}})!
			const result = await renderToPdf(doc_node, {margin: {top: 50, right: 50, bottom: 50, left: 50}})

			const gen: GeneratedDoc = {
				short: doc.short,
				seq_typ: doc.seq_typ,
				author: str(doc.doc_row['payout_doc.author']),
				net_total: pending_doc_net_total(doc),
				ccy: str(doc.doc_row['payout_doc.ccy']) || 'SEK',
				line_count: doc.lines.length,
				pdf_bytes: result.pdfBytes,
				page_count: result.pageCount,
			}

			setState({stage: 'done', filename, results: [gen], sheets, allPending: pending, allExported: exported, buffer})
		} catch (err) {
			setState({stage: 'error', message: err instanceof Error ? err.message : String(err)})
		}
	}, [state])

	// -- download zip --

	const handleDownloadZip = useCallback(async () => {
		if (state.stage !== 'done') return
		const {results} = state
		const JSZip = (await import('jszip')).default
		const zip = new JSZip()

		for (const r of results) zip.file(`${r.short}.pdf`, r.pdf_bytes)

		const now_iso = new Date().toISOString().slice(0, 10)
		const tsv_header = 'doc\texported_at\tauthor\ttotal_amount\tccy\tline_count\tfile_name'
		const tsv_rows = results.map(r =>
			`${r.short}\t${now_iso}\t${r.author}\t${Math.round(r.net_total * 100) / 100}\t${r.ccy}\t${r.line_count}\t${r.short}.pdf`,
		)
		zip.file('export_batch.tsv', [tsv_header, ...tsv_rows].join('\n'))

		const blob = await zip.generateAsync({type: 'blob'})
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `ftr-export-${now_iso}.zip`
		a.click()
		URL.revokeObjectURL(url)
	}, [state])

	// -- PDF preview --

	const handlePreviewPdf = useCallback((doc: PendingDoc | GeneratedDoc, sheets?: typeof currentSheets) => {
		if ('pdf_bytes' in doc) {
			const blob = new Blob([doc.pdf_bytes], {type: 'application/pdf'})
			const url = URL.createObjectURL(blob)
			setModal({type: 'pdf', short: doc.short, url})
			return
		}
		;(async () => {
			const dok_typer = sheets?.find(s => s.name === 'Dok.typer')
			const kontakter = sheets?.find(s => s.name === 'Kontakter')
			const basic_inp = build_basic_doc_input(doc, dok_typer, kontakter)
			const {renderToPdf} = await import('@ts/mod/pdf/layout-renderer')
			const {processTemplate} = await import('@ts/mod/layout/template')
			const {sa_template_def, sf_template_def, basic_doc_to_template_data} = await import('@ts/mod/ftr_f19/basic/mod')
			const template_data = basic_doc_to_template_data(basic_inp)
			const template_def = doc.seq_typ === 'SF' ? sf_template_def : sa_template_def
			const doc_node = processTemplate(template_def, {...template_data, _assets: {}})!
			const result = await renderToPdf(doc_node, {margin: {top: 50, right: 50, bottom: 50, left: 50}})
			const blob = new Blob([result.pdfBytes], {type: 'application/pdf'})
			const url = URL.createObjectURL(blob)
			setModal({type: 'pdf', short: doc.short, url})
		})()
	}, [])

	// -- modal --

	const closeModal = useCallback(() => {
		if (modal?.type === 'pdf') URL.revokeObjectURL(modal.url)
		setModal(null)
	}, [modal])

	// -- download example --

	const handleDownloadExample = useCallback(async () => {
		setState({stage: 'loading'})
		try {
			const {generate_csa_ex_workbook} = await import('./generate_example')
			const buf = await generate_csa_ex_workbook()
			const blob = new Blob([buf], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'})
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = 'ftr_f19_example.xlsx'
			a.click()
			URL.revokeObjectURL(url)
			setState({stage: 'idle'})
		} catch (err) {
			setState({stage: 'error', message: err instanceof Error ? err.message : String(err)})
		}
	}, [])

	// -- reset --

	const handleReset = useCallback(() => {
		closeModal()
		setSelected(new Set())
		setState({stage: 'idle'})
	}, [closeModal])

	// -- derived data from current state --

	const currentSheets =
		  state.stage === 'parsed' ? state.sheets
		: state.stage === 'done' ? state.sheets
		: state.stage === 'generating' ? state.sheets
		: undefined
	const currentBuffer =
		  state.stage === 'parsed' ? state.buffer
		: state.stage === 'done' ? state.buffer
		: state.stage === 'generating' ? state.buffer
		: undefined
	const currentFilename =
		  state.stage === 'idle' || state.stage === 'loading' || state.stage === 'error' ? undefined
		: state.filename
	const currentPending = state.stage === 'parsed' ? state.pending : state.stage === 'done' ? state.allPending : []
	const currentExported = state.stage === 'parsed' ? state.exported : state.stage === 'done' ? state.allExported : []
	const generatedResults = state.stage === 'done' ? state.results : []

	// -- render --

	const showContent = (state.stage === 'parsed' || state.stage === 'done') && currentSheets && currentBuffer && currentFilename

	return (
		<div style={S.root}>
			{/* header */}
			<div style={S.header}>
				<h1 style={S.title}>FTR F19</h1>
				{state.stage !== 'idle' && state.stage !== 'loading' && (
					<button style={S.resetBtn} onClick={handleReset}>
						<Icon icon="tabler:arrow-left" size={14} /> Ny fil
					</button>
				)}
			</div>

			{/* modal */}
			{modal && <Modal modal={modal} onClose={closeModal} />}

			{/* idle */}
			{state.stage === 'idle' && (
				<div style={S.center}>
					<DropArea onDrop={handleDrop} onFileSelect={handleFileSelect} fileInputRef={fileInputRef} />
					<div style={S.or}>eller</div>
					<button style={S.exampleBtn} onClick={handleDownloadExample}>
						<Icon icon="tabler:download" size={16} /> Ladda ner exempelarbetsbok
					</button>
				</div>
			)}

			{state.stage === 'loading' && (
				<div style={S.center}><div style={S.spinner} /><p>Läser fil...</p></div>
			)}

			{state.stage === 'error' && (
				<div style={S.center}>
					<div style={S.errorBox}>{state.message}</div>
					<button style={S.btn} onClick={handleReset}>Försök igen</button>
				</div>
			)}

			{state.stage === 'generating' && (
				<div style={S.center}>
					<div style={S.spinner} />
					<p>Genererar PDF {state.progress + 1} / {state.total}...</p>
					<p style={S.dim}>{state.selected[state.progress]?.short}</p>
				</div>
			)}

			{/* parsed or done: sheet tabs + doc table */}
			{showContent && (
				<div style={S.content}>
					<SheetTabs
						sheets={currentSheets}
						onClickSheet={name => {
							const sheetIndex = currentSheets.findIndex(s => s.name === name)
							setModal({type: 'excel', buffer: currentBuffer, filename: currentFilename, sheet: name, sheetIndex: sheetIndex >= 0 ? sheetIndex : undefined})
						}}
					/>
					<DocTable
						pending={currentPending}
						exported={currentExported}
						generatedResults={generatedResults}
						selected={selected}
						onToggleSelect={toggleSelect}
						onToggleSelectAll={toggleSelectAll}
						onPreviewPdf={handlePreviewPdf}
						onOpenExcel={(sheet?: string, highlight?: HighlightTarget) => {
							const sheetIndex = sheet ? currentSheets.findIndex(s => s.name === sheet) : undefined
							setModal({type: 'excel', buffer: currentBuffer, filename: currentFilename, sheet, sheetIndex: sheetIndex != null && sheetIndex >= 0 ? sheetIndex : undefined, highlight})
						}}
						onOpenExcelBuffer={(buffer, filename) => {
							setModal({type: 'excel', buffer, filename})
						}}
						onGenerate={handleGenerate}
						onGenerateOne={handleGenerateOne}
						onDownloadZip={handleDownloadZip}
						sheets={currentSheets}
						isDone={state.stage === 'done'}
						isParsed={state.stage === 'parsed'}
					/>
				</div>
			)}
		</div>
	)
}
